// Simple encryption/decryption utilities for local storage
// Note: This is basic obfuscation, not military-grade encryption
// For production, consider using a library like crypto-js

import type { TokenInfo } from '../services/types';
import {
  areCookiesAvailable,
  saveTokenInfoToCookies,
  getTokenInfoFromCookies,
  updateTokenInfoInCookies,
  clearTokenInfoFromCookies,
} from './cookieUtils';

export const REMEMBER_ME_STORAGE_KEY = 'cv_remember_me';
const STORAGE_SECRET_KEY = import.meta.env.VITE_STORAGE_SECRET_KEY || '';

// Check if cookies are available at module load
const USE_COOKIES = areCookiesAvailable();

/**
 * Simple XOR-based encryption for obfuscating data in localStorage
 * Not cryptographically secure, but prevents casual inspection
 */
function simpleEncrypt(text: string, key: string): string {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    const charCode = text.charCodeAt(i) ^ key.charCodeAt(i % key.length);
    result += String.fromCharCode(charCode);
  }
  return btoa(result); // Base64 encode
}

/**
 * Decrypt XOR-encrypted data
 */
function simpleDecrypt(encrypted: string, key: string): string {
  try {
    const decoded = atob(encrypted); // Base64 decode
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
      const charCode = decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length);
      result += String.fromCharCode(charCode);
    }
    return result;
  } catch {
    return '';
  }
}

interface RememberMeData {
  email: string;
  rememberMe: boolean;
}

/**
 * Save email and remember me state to localStorage (encrypted)
 */
export function saveRememberMe(email: string, rememberMe: boolean): void {
  try {
    if (rememberMe && email) {
      const data: RememberMeData = { email, rememberMe };
      const encrypted = simpleEncrypt(JSON.stringify(data), STORAGE_SECRET_KEY);
      localStorage.setItem(REMEMBER_ME_STORAGE_KEY, encrypted);
    } else {
      // If remember me is false, clear the stored data
      clearRememberMe();
    }
  } catch (error) {
    console.error('Failed to save remember me data:', error);
  }
}

/**
 * Load email and remember me state from localStorage
 */
export function loadRememberMe(): RememberMeData | null {
  try {
    const encrypted = localStorage.getItem(REMEMBER_ME_STORAGE_KEY);
    if (!encrypted) {
      return null;
    }
    const decrypted = simpleDecrypt(encrypted, STORAGE_SECRET_KEY);
    if (!decrypted) {
      // If decryption fails, clear invalid data
      clearRememberMe();
      return null;
    }
    return JSON.parse(decrypted) as RememberMeData;
  } catch (error) {
    console.error('Failed to load remember me data:', error);
    clearRememberMe();
    return null;
  }
}

/**
 * Clear remember me data from localStorage
 */
export function clearRememberMe(): void {
  try {
    localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear remember me data:', error);
  }
}

/**
 * Check if localStorage is available and working
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

// ========================================
// Auth Info Storage
// ========================================

/**
 * Storage key for auth info
 */
const AUTH_INFO_KEY = 'auth_info';

/**
 * Get authentication info from storage
 * Priority: Cookies (if available) -> Encrypted localStorage/sessionStorage
 */
export function getTokenInfo(): TokenInfo | null {
  try {
    // Try cookies first (most secure)
    if (USE_COOKIES) {
      const tokenInfo = getTokenInfoFromCookies();
      if (tokenInfo) {
        return tokenInfo;
      }
    }
    
    // Fallback to encrypted storage
    const encrypted = localStorage.getItem(AUTH_INFO_KEY) || sessionStorage.getItem(AUTH_INFO_KEY);
    if (!encrypted) return null;
    
    // Try decrypting
    const decrypted = simpleDecrypt(encrypted, STORAGE_SECRET_KEY);
    if (!decrypted) {
      // If decryption fails, clear invalid data
      clearTokenInfo();
      return null;
    }
    return JSON.parse(decrypted) as TokenInfo;
  } catch (error) {
    console.error('Failed to parse auth info:', error);
    clearTokenInfo();
    return null;
  }
}

/**
 * Save authentication info to storage
 * Priority: Cookies (if available) -> Encrypted localStorage/sessionStorage
 * @param tokenInfo Authentication info to save
 * @param rememberMe Whether to use localStorage (true) or sessionStorage (false)
 */
export function saveTokenInfo(tokenInfo: TokenInfo, rememberMe: boolean = false): void {
  try {
    // Try cookies first (most secure)
    if (USE_COOKIES) {
      const success = saveTokenInfoToCookies(tokenInfo, rememberMe);
      if (success) {
        // Also clear any existing storage to avoid conflicts
        localStorage.removeItem(AUTH_INFO_KEY);
        sessionStorage.removeItem(AUTH_INFO_KEY);
        return;
      }
      // If cookie save fails, fall through to encrypted storage
    }
    // Fallback to encrypted storage
    const storage = rememberMe ? localStorage : sessionStorage;
    const encrypted = simpleEncrypt(JSON.stringify(tokenInfo), STORAGE_SECRET_KEY);
    storage.setItem(AUTH_INFO_KEY, encrypted);
    // Clear from the other storage to avoid conflicts
    const otherStorage = rememberMe ? sessionStorage : localStorage;
    otherStorage.removeItem(AUTH_INFO_KEY);
  } catch (error) {
    clearTokenInfo();
    console.error('Failed to save auth info:', error);
  }
}

/**
 * Update auth info with new tokens (preserves storage location)
 * Used internally by apiClient when refreshing tokens
 */
export function updateTokenInfo(newtokenInfo: TokenInfo): void {
  try {
    // If using cookies, update there
    if (USE_COOKIES) {
      const existingCookieTokenInfo = getTokenInfoFromCookies();
      if (existingCookieTokenInfo) {
        updateTokenInfoInCookies(newtokenInfo);
        return;
      }
    }
    
    // Otherwise update in storage
    const useLocalStorage = !!localStorage.getItem(AUTH_INFO_KEY);
    const storage = useLocalStorage ? localStorage : sessionStorage;
    const encrypted = simpleEncrypt(JSON.stringify(newtokenInfo), STORAGE_SECRET_KEY);
    storage.setItem(AUTH_INFO_KEY, encrypted);
  } catch (error) {
    clearTokenInfo();
    console.error('Failed to update auth info:', error);
  }
}

/**
 * Clear all authentication info from storage (cookies + encrypted storage)
 */
export function clearTokenInfo(): void {
  try {
    // Clear cookies
    if (USE_COOKIES) {
      clearTokenInfoFromCookies();
    }
    
    // Clear storage
    localStorage.removeItem(AUTH_INFO_KEY);
    sessionStorage.removeItem(AUTH_INFO_KEY);
    localStorage.removeItem("companyId");
    localStorage.removeItem("companyName");
  } catch (error) {
    console.error('Failed to clear auth info:', error);
  }
}

// ========================================
// Global Page Size Storage
// ========================================

const GLOBAL_PAGE_SIZE_KEY = 'global_page_size';

export function saveGlobalPageSize(size: number): void {
  try {
    localStorage.setItem(GLOBAL_PAGE_SIZE_KEY, String(size));
  } catch (error) {
    console.error('Failed to save global page size:', error);
  }
}

export function getGlobalPageSize(): number | null {
  try {
    const value = localStorage.getItem(GLOBAL_PAGE_SIZE_KEY);
    if (!value) return null;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? null : parsed;
  } catch (error) {
    console.error('Failed to get global page size:', error);
    return null;
  }
}

// ========================================
// Selected Fiscal Year Storage
// ========================================

const SELECTED_FISCAL_YEAR_KEY = 'selected_fiscal_year';

export function saveSelectedFiscalYear(year: number): void {
  try {
    localStorage.setItem(SELECTED_FISCAL_YEAR_KEY, String(year));
  } catch (error) {
    console.error('Failed to save selected fiscal year:', error);
  }
}

export function getSelectedFiscalYear(): number | null {
  try {
    const value = localStorage.getItem(SELECTED_FISCAL_YEAR_KEY);
    if (!value) return null;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? null : parsed;
  } catch (error) {
    console.error('Failed to get selected fiscal year:', error);
    return null;
  }
}

// ========================================
// Sidebar Collapsed State
// ========================================

const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed';

export function saveSidebarCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(collapsed));
  } catch (error) {
    console.error('Failed to save sidebar collapsed state:', error);
  }
}

export function getSidebarCollapsed(): boolean {
  try {
    const value = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (!value) return false;
    return JSON.parse(value) === true;
  } catch (error) {
    console.error('Failed to get sidebar collapsed state:', error);
    return false;
  }
}

// ========================================
// Gap Auto-Creation Prompt Storage
// ========================================

/**
 * Storage key for tracking which Gap IDs have been prompted
 */
const GAP_PROMPTED_IDS_KEY = 'gap_prompted_ids';

/**
 * Get the list of Gap IDs that have already been prompted
 */
export function getPromptedGapIds(): string[] {
  try {
    const data = localStorage.getItem(GAP_PROMPTED_IDS_KEY);
    if (!data) return [];
    return JSON.parse(data) as string[];
  } catch (error) {
    console.error('Failed to get prompted gap IDs:', error);
    return [];
  }
}

